import { Layers } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Notify } from "@/components/ui/notify";
import { useMachineOee } from "@/features/oee/hooks";
import { MachineOeeBreakdown } from "@/features/oee/MachineOeeBreakdown";
import { PlannedBasisBadge } from "@/features/oee/PlannedBasisBadge";
import { useLineTrackedByMachine } from "@/hooks/useLineTrackedByMachine";
import type { MachineDetail } from "@/features/lines/hooks";
import type { MachineOeeBreakdownItem } from "@opus-dt/shared-types";

interface Props {
  lineId: string;
  machines: MachineDetail[];
  onSelectMachine: (machine: MachineDetail) => void;
}

// Máquina-gargalo = menor valor não-nulo do componente. Só destaca quando há ≥2 valores para
// comparar (code-review #10): com um único valor não há "pior" a apontar.
function worstId(
  items: MachineOeeBreakdownItem[],
  pick: (i: MachineOeeBreakdownItem) => number | null
) {
  const valued = items
    .map((it) => ({ id: it.machineId, v: pick(it) }))
    .filter((x): x is { id: string; v: number } => x.v !== null);
  if (valued.length < 2) return null;
  return valued.reduce((min, x) => (x.v < min.v ? x : min)).id;
}

// Card de decomposição por máquina na tela da linha. Uma linha por máquina
// (Disponibilidade + Qualidade + refugo), destaca a PIOR em cada componente (gargalo).
// Só aparece em linha rastreada por máquina (algum item é contador) — senão não renderiza.
export function MachineDecompositionCard({ lineId, machines, onSelectMachine }: Props) {
  const trackedByMachine = useLineTrackedByMachine(lineId);
  const oeeQuery = useMachineOee(lineId, "24h", { enabled: trackedByMachine });
  const items = oeeQuery.data?.items ?? [];
  // Card só existe em linha rastreada por máquina. Erro genuíno de fetch, porém, não pode
  // virar `null` silencioso igual ao caso "sem itens" — só some quando de fato não se aplica.
  if (!trackedByMachine || (items.length === 0 && !oeeQuery.isError)) return null;

  const worstAvailabilityId = worstId(items, (i) => i.availability);
  const worstQualityId = worstId(items, (i) => (i.isProductionCounter ? i.quality : null));

  return (
    <section aria-labelledby="machine-decomp-h2" className="space-y-3">
      <h2
        id="machine-decomp-h2"
        className="flex items-center gap-2 text-[18px] font-semibold tracking-tight text-text-primary"
      >
        <Layers className="h-4 w-4" aria-hidden />
        Decomposição por máquina
      </h2>
      <Card className="rounded-2xl px-[22px] py-5">
        {oeeQuery.isError ? (
          <Notify.QueryError
            variant="inline"
            error={oeeQuery.error}
            onRetry={() => void oeeQuery.refetch()}
          />
        ) : (
          <>
            <p className="mb-3 text-xs text-muted-foreground">
              Disponibilidade e Qualidade por máquina — o realce aponta o gargalo de cada
              componente. O OEE composto por máquina ainda não inclui Desempenho.
            </p>
            {/* A base do tempo planejado é propriedade da LINHA (é o calendário dela que
                resolve), então aparece UMA vez no card. Repeti-la em cada máquina sugeriria que
                máquinas da mesma linha podem ter bases diferentes — a incoerência que a
                decomposição existe para evitar. */}
            {oeeQuery.data ? (
              <PlannedBasisBadge
                basis={oeeQuery.data.plannedBasis}
                calendarSource={oeeQuery.data.calendarSource}
                lineId={lineId}
                className="mb-3"
              />
            ) : null}
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {items.map((item) => {
                const machine = machines.find((m) => m.id === item.machineId);
                return (
                  <MachineOeeBreakdown
                    key={item.machineId}
                    item={item}
                    worstAvailability={item.machineId === worstAvailabilityId}
                    worstQuality={item.machineId === worstQualityId}
                    onClick={machine ? () => onSelectMachine(machine) : undefined}
                  />
                );
              })}
            </div>
          </>
        )}
      </Card>
    </section>
  );
}
